import React from 'react';
import { Landmark, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { EmptyState, StatusPill } from '../../../components/app';
import { cn } from '../../../lib/utils';
import { SettlementsTableProps } from '../types';
import { formatDay } from '../format';

export function SettlementsTable({ settlements, isLoading, onEdit, onDelete, formatMoney }: SettlementsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Дата зачисления</TableHead>
          <TableHead>Провайдер</TableHead>
          <TableHead className="text-right">Сумма</TableHead>
          <TableHead>Комментарий</TableHead>
          <TableHead className="w-[1%]">
            <span className="sr-only">Действия</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 5 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: 5 }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full max-w-28" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : settlements.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={5}>
              <EmptyState
                icon={<Landmark />}
                title="Поступлений за период нет"
                description="Импортируйте выписку банка или заведите Поступление вручную."
              />
            </TableCell>
          </TableRow>
        ) : (
          settlements.map((settlement) => (
            <TableRow key={settlement.id}>
              <TableCell className="whitespace-nowrap tabular-nums">
                <span className="flex items-center gap-2">
                  {formatDay(settlement.date)}
                  {settlement.bank_fingerprint && (
                    <StatusPill
                      size="sm"
                      tone="pos"
                      icon={<Landmark className="size-3" aria-hidden />}
                      title={`Подтверждено банком${settlement.bank_document ? ` · документ №${settlement.bank_document}` : ''}`}
                    >
                      банк
                    </StatusPill>
                  )}
                </span>
              </TableCell>
              <TableCell className="font-semibold">{settlement.provider?.name || '—'}</TableCell>
              <TableCell
                className={cn('text-right font-semibold tabular-nums whitespace-nowrap', settlement.amount < 0 && 'text-neg')}
              >
                {formatMoney(settlement.amount, settlement.currency)}
              </TableCell>
              <TableCell className="max-w-xs truncate text-text-2">{settlement.comment || '—'}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon-sm" onClick={() => onEdit(settlement)} aria-label="Изменить поступление">
                    <Pencil className="text-text-2" />
                  </Button>
                  <Button variant="ghost" size="icon-sm" onClick={() => onDelete(settlement.id)} aria-label="Удалить поступление">
                    <Trash2 className="text-neg" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
